import React, { useEffect, useRef, useState, useMemo } from "react";
import * as echarts from "echarts";

// Get the financial year string for a given date
const getFinancialYear = (dateStr) => {
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = date.getMonth();
  const fyStart = month >= 3 ? year : year - 1;
  return `FY ${fyStart}-${fyStart + 1}`;
};

const getFinancialYears = (projects, includePreviousFY = false) => {
  const fySet = new Set();
  let minFYYear = new Date().getFullYear();

  projects.forEach((p) => {
    const date = new Date(p.project_start_date);
    const year = date.getFullYear();
    const month = date.getMonth();
    const fyStart = month >= 3 ? year : year - 1;
    const fyLabel = `FY ${fyStart}-${fyStart + 1}`;

    fySet.add(fyLabel);
    if (fyStart < minFYYear) {
      minFYYear = fyStart;
    }
  });

  if (includePreviousFY) {
    fySet.add(`FY ${minFYYear - 1}-${minFYYear}`);
  }

  return [...fySet].sort((a, b) => {
    const aStart = +a.split(" ")[1].split("-")[0];
    const bStart = +b.split(" ")[1].split("-")[0];
    return bStart - aStart;
  });
};

const StatusPieChart = ({ projects, statusOptions }) => {
  const chartRef = useRef(null);

  // Get financial years and default to current FY
  const financialYears = useMemo(() => getFinancialYears(projects), [projects]);
  const currentFY = getFinancialYear(new Date());
  const [selectedFY, setSelectedFY] = useState(currentFY);

  const filteredProjects = useMemo(() => {
    if (!selectedFY) return projects;
    return projects.filter(
      (p) => getFinancialYear(p.project_start_date) === selectedFY
    );
  }, [projects, selectedFY]);

  useEffect(() => {
    if (!filteredProjects || filteredProjects.length === 0) return;

    const chartInstance = echarts.init(chartRef.current);

    // Calculate status counts
    const statusCounts = filteredProjects.reduce((acc, project) => {
      if (project.phase_scopes) {
        const statuses = project.phase_scopes.split(", ").map((s) => s.trim());
        statuses.forEach((status) => {
          acc[status] = (acc[status] || 0) + 1;
        });
      }
      return acc;
    }, {});

    const totalProjects = Object.values(statusCounts).reduce(
      (a, b) => a + b,
      0
    );

    const statusLabels = Object.keys(statusCounts);
    const chartData = statusLabels.map((status) => {
      const statusObj = statusOptions.find((option) => option.name === status);
      return {
        name: status,
        value: statusCounts[status],
        itemStyle: { color: statusObj ? statusObj.color : "#CCCCCC" },
      };
    });

    const option = {
      tooltip: {
        trigger: "item",
        formatter: "{b}: {c}",
      },
      legend: {
        orient: "vertical",
        bottom: "10px",
        right: "right",
        center: "center",
      },
      series: [
        {
          name: "Project Status",
          type: "pie",
          radius: ["50%", "80%"],
          avoidLabelOverlap: false,
          label: {
            show: false,
          },
          emphasis: {
            label: {
              show: false,
            },
          },
          labelLine: {
            show: false,
          },
          data: chartData,
        },
      ],
      graphic: {
        type: "text",
        left: "center",
        top: "center",
        style: {
          text: `${totalProjects}`,
          textAlign: "center",
          fontSize: 30,
          fontWeight: "bold",
          fill: "#333",
        },
      },
    };

    chartInstance.setOption(option);

    return () => {
      chartInstance.dispose();
    };
  }, [filteredProjects, statusOptions]);

  return (
    <div className="project-status">
      <h2 className="pie-header" style={{ top: "-1px" }}>
        Phase Scope
      </h2>
      <select
        className="pm-fy-dd"
        value={selectedFY}
        onChange={(e) => setSelectedFY(e.target.value)}
      >
        <option value="">All Years</option>
        {financialYears.map((fy) => (
          <option key={fy} value={fy}>
            {fy}
          </option>
        ))}
      </select>
      <div ref={chartRef} style={{ width: "100%", height: "400px" }} />
    </div>
  );
};

export default StatusPieChart;
